import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import { getDiscountPercent, getPriceAsFloat } from "@core/pricing"
import { get } from "lodash"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import type { Label } from "@gatsby-plugin-generic-page/fragments/label"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { SaleConfiguration } from "~/fragments/saleConfigurations"
import useSaleConfigurationState from "~/hooks/useSaleConfigurationState"
import isEmpty from "~/lib/isEmpty"
import { getProductPricing } from "~/lib/pricing"
import { prepareComponentValues } from "~/utils"

export type SummaryProps = {
  label?: Omit<Label, "__typename">
  tooltip?: string
  oldPrice?: string
  oldPriceText?: string
  priceBig: {
    integer: string
    fraction: string
    suffix: string
    currency: string
  }
  periodInfo: PeriodInfo | null
  hasPromotion: boolean
  priceValue: string
  lowestPrice: string
  lowestPricePercent: string
  selectedPeriodInfo: PeriodInfo
  selectedPriceInfo: CloudBluePlan["flatData"]["priceInfo"]
}

export default function useSummary(
  saleConfiguration?: SaleConfiguration
): SummaryProps | null {
  const { currentPriceType, isNettoPriceType } = usePriceTypeContext()

  const saleConfigurationState = useSaleConfigurationState(
    saleConfiguration?.id || ""
  )

  const defaultPlan = get(saleConfiguration, "flatData.defaultCloudBluePlan[0]")

  if (
    isEmpty(saleConfiguration) ||
    isEmpty(saleConfigurationState) ||
    isEmpty(defaultPlan)
  ) {
    return null
  }

  const { oldPriceText, isOldPriceTextInPercent, label } =
    saleConfiguration.flatData

  const { promoText } = saleConfiguration.flatData.config

  const componentValues = prepareComponentValues(
    saleConfiguration.flatData.optionsMapping || [],
    saleConfigurationState.options,
    defaultPlan
  )

  if (isEmpty(componentValues)) {
    return null
  }

  const searchedPeriod = defaultPlan.flatData.periods.find(
    (item) => item.period_name === componentValues?.period
  )

  const defaultPeriod = defaultPlan.flatData.periods.find(
    (item) => item.default ?? false
  )

  const period = searchedPeriod || defaultPeriod

  const periodInfo = period ? new PeriodInfo(period) : null

  const { priceValues } = componentValues

  const { priceValue, lowestPrice, lowestPricePercent, hasPromotion } =
    priceValues

  const currentPrice = getProductPricing(priceValues, currentPriceType)

  const discount = (
    getPriceAsFloat(priceValues.regularPrice.gross) -
    getPriceAsFloat(priceValues.price.gross)
  ).toFixed(2)

  const discountPercent = getDiscountPercent(
    priceValues.regularPrice,
    priceValues.price,
    currentPriceType
  )

  const discountValue = `${discount
    .toString()
    .replace(/\./g, currentPrice.currencySeparator)} ${currentPrice.currency}`

  const oldPriceTextValue =
    oldPriceText &&
    `${oldPriceText} ${
      isOldPriceTextInPercent ? discountPercent : discountValue
    }`

  const productTileLabel: SummaryProps["label"] = !isEmpty(label)
    ? {
        text: label.text,
        variant: label.variant,
      }
    : undefined

  const promoTextTileLabel: SummaryProps["label"] = !isEmpty(promoText)
    ? {
        text: promoText,
        variant: "notify",
      }
    : undefined

  const tooltip = get(
    saleConfiguration,
    "flatData.defaultCloudBluePlan[0].flatData.promotionPriceTooltip[0].flatData.text"
  )

  return {
    label: productTileLabel || promoTextTileLabel,
    tooltip,
    oldPriceText: oldPriceTextValue ?? undefined,
    priceBig: {
      integer: currentPrice.priceDecimals,
      fraction: currentPrice.currencySeparator + currentPrice.priceCents,
      suffix: isNettoPriceType
        ? `${_t("prices.netto")} ${componentValues.periodText}`
        : `${_t("prices.brutto")} ${componentValues.periodText} (${_t(
            "marketplace.withTaxRate"
          )})`,
      currency: currentPrice.currency,
    },
    periodInfo,
    hasPromotion,
    priceValue,
    lowestPrice,
    lowestPricePercent,
    selectedPeriodInfo: componentValues.selectedPeriodInfo,
    selectedPriceInfo: componentValues.selectedPriceInfo,
  }
}
